import { DayPicker } from 'react-day-picker'
import { Input } from '@/app/[locale]/(ui)/input'
import { useEffect, useId, useState } from 'react'
import { nb } from 'react-day-picker/locale'
import { Popover, PopoverContent, PopoverTrigger } from '@/app/[locale]/(ui)/popover'
import { isEqual } from 'date-fns'
import {
  getNextAvailablePickupDate,
  isPickupDateDisabled,
  normalizePickupDate,
  OSLO_TIME_ZONE,
  toOsloDateString,
} from '@/lib/pickup-date'
import { CalendarDays } from 'lucide-react'
import { useTranslations } from 'next-intl'

export const DateInput = ({
  className,
  date,
  minDate = new Date(),
  label,
  onChange,
}: {
  className?: string
  date?: Date
  minDate?: Date
  label?: string
  onChange: (date: Date) => void
}) => {
  const t = useTranslations()
  const [isOpen, setIsOpen] = useState(false)
  const id = useId()
  const fallbackDate = getNextAvailablePickupDate(new Date(), minDate)
  const selectedDate = date ? normalizePickupDate(date, minDate) : fallbackDate

  useEffect(() => {
    if (!date || !isEqual(selectedDate, date)) {
      onChange(selectedDate)
    }
  }, [date, onChange, selectedDate])

  const onDateChange = (date?: Date) => {
    if (date) {
      onChange(date)
      setIsOpen(false)
    }
  }

  return (
    <div className={className}>
      {label && <label htmlFor={id}>{label}</label>}
      <div className="relative">
        <Input
          id={id}
          value={selectedDate ? toOsloDateString(selectedDate) : undefined}
          placeholder={t('Select_date')}
          onClick={() => setIsOpen(true)}
          readOnly
        />
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger
            render={
              <button
                type="button"
                className="absolute inset-0 flex size-full justify-end"
                onClick={() => setIsOpen(true)}
              >
                <CalendarDays size={16} className="mr-[.6em] mt-[.8em]" />
              </button>
            }
          />
          <PopoverContent align="start">
            <DayPicker
              mode="single"
              locale={nb}
              timeZone={OSLO_TIME_ZONE}
              weekStartsOn={1}
              showOutsideDays
              classNames={{
                chevron: 'fill-black',
                caption_label: 'text-sm font-semibold',
                day: 'text-sm font-semibold rounded-lg',
                button_next: 'ml-2 bg-gray-200 rounded-md',
                button_previous: 'bg-gray-200 rounded-md',
                day_button: 'p-2 leading-[1] size-full text-center',
                today: 'bg-gray-200',
                selected: '!bg-black text-white',
                disabled: 'opacity-30',
              }}
              selected={selectedDate}
              defaultMonth={selectedDate ?? new Date()}
              onSelect={onDateChange}
              disabled={(day) => isPickupDateDisabled(day, minDate)}
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  )
}
